import _ from 'lodash';

import { OrderProduct, Product, ProductSize } from '../../api-client';
import {
	IAestheticLineGroup,
	IProductSizeAggregate,
	IShoppingDateGroup,
	IShoppingFacilityGroup,
} from '../../common/models/Shopping';
import { formatCurrency } from './currency-enchanter';
import { formatStandardDate } from './datetime-enchanter';

export const groupProductsByFacility = (orderProducts?: OrderProduct[]) => {
	if (!orderProducts) {
		return [];
	}

	let facilityGroups: IShoppingFacilityGroup[] = [];

	_.forEach(orderProducts, (orderProduct) => {
		let facilityGroupItem = _.find(
			facilityGroups,
			(entry) => entry.id === orderProduct.facility?.id,
		);
		if (facilityGroupItem && facilityGroupItem.products) {
			facilityGroupItem.products.push(orderProduct);
		} else {
			facilityGroups.push({
				...orderProduct.facility,
				products: [orderProduct],
			});
		}
	});

	return _.orderBy(facilityGroups, (entry) => entry.name);
};

export const groupProductsByFacilityDate = (orderProducts?: OrderProduct[]) => {
	if (!orderProducts) {
		return [];
	}

	let dateGroups: IShoppingDateGroup[] = [];

	_.forEach(orderProducts, (orderProduct) => {
		let dateGroupItem = _.find(
			dateGroups,
			(entry) => entry.id === orderProduct.facility?.id,
		);
		if (dateGroupItem && dateGroupItem.deliveryDates) {
			let deliveryDateItem = _.find(
				dateGroupItem.deliveryDates,
				(entry) =>
					formatStandardDate(entry.deliveryDate) ===
					formatStandardDate(orderProduct.deliveryDate),
			);

			if (deliveryDateItem && deliveryDateItem.products) {
				deliveryDateItem.products.push(orderProduct);
			} else {
				dateGroupItem.deliveryDates.push({
					deliveryDate: orderProduct.deliveryDate,
					products: [orderProduct],
				});
			}

			dateGroupItem.deliveryDates = _.orderBy(
				dateGroupItem.deliveryDates,
				(entry) => entry.deliveryDate,
			);
		} else {
			dateGroups.push({
				...orderProduct.facility,
				deliveryDates: [
					{
						deliveryDate: orderProduct.deliveryDate,
						products: [orderProduct],
					},
				],
			});
		}
	});

	return _.orderBy(dateGroups, (entry) => entry.name);
};

export const groupProductsByProduct = (orderProducts?: OrderProduct[]) => {
	if (!orderProducts) {
		return [];
	}

	let productGroups: OrderProduct[] = [];

	_.forEach(orderProducts, (orderProduct) => {
		let productGroupItem = _.find(
			productGroups,
			(entry) => entry.id === orderProduct.id,
		);
		if (productGroupItem) {
			productGroupItem = {
				...productGroupItem,
				sizes:
					productGroupItem.sizes &&
					productGroupItem.sizes.map((productSize) => ({
						...productSize,
						quantity: manipulateSizeQuantity(productSize, orderProduct, true),
					})),
			};

			productGroups = _.filter(
				productGroups,
				(entry) => entry.id !== orderProduct.id,
			);
			productGroups.push(productGroupItem);
		} else {
			productGroups.push(orderProduct);
		}
	});

	return productGroups;
};

export const aggregateProductSizes = (orderProducts?: OrderProduct[]) => {
	if (!orderProducts) {
		return [];
	}

	let productSizes: IProductSizeAggregate[] = [];

	_.forEach(orderProducts, (orderProduct, orderProductKey) => {
		let productSizeAggregate: IProductSizeAggregate = {
			productId: orderProduct.id,
			productRowId: orderProduct.rowId,
			productCatCode: orderProduct.catalogCode,
			productImageUrl: orderProduct.imageUrl,
			productDeliveryDate: orderProduct.deliveryDate,
			productFacility: orderProduct.facility,
		};

		_.forEach(orderProduct.sizes, (orderProductSize, orderProductSizeKey) => {
			if (orderProductSize.quantity && orderProductSize.quantity > 0) {
				productSizes.push({
					_arrayKey: `size-${orderProductKey}-${orderProductSizeKey}`,
					...productSizeAggregate,
					...orderProductSize,
				});
			}
		});
	});

	return _.sortBy(productSizes, (entry) => entry.code);
};

export const groupProductsByAestheticLine = (products?: Product[]) => {
	if (!products) {
		return [];
	}

	let aestheticLineGroups: IAestheticLineGroup[] = [];

	_.forEach(products, (product) => {
		let aestheticLineGroupItem = _.find(
			aestheticLineGroups,
			(entry) => entry.aestheticLine === product.aestheticLine,
		);
		if (aestheticLineGroupItem && aestheticLineGroupItem.products) {
			aestheticLineGroupItem.products.push(product);
		} else {
			aestheticLineGroups.push({
				aestheticLine: product.aestheticLine,
				products: [product],
			});
		}
	});

	return _.orderBy(aestheticLineGroups, (entry) => entry.aestheticLine);
};

interface ISizesTotal {
	amount: number;
	amountFormat: string;
}

export const orderTotalByProducts = (
	orderProducts?: OrderProduct[],
): ISizesTotal => {
	let orderTotal = 0;
	let orderCurrency: string | undefined;

	if (!orderProducts) {
		return {
			amount: orderTotal,
			amountFormat: formatCurrency(orderTotal, orderCurrency),
		};
	}

	_.forEach(orderProducts, (orderProduct) => {
		if (orderProduct.sizes) {
			let sizesAmount = _.map(orderProduct.sizes, (sizeData) => {
				if (!sizeData.priceNet || sizeData.priceNet.amount === -1) {
					return 0;
				} else {
					if (!orderCurrency) {
						orderCurrency = sizeData.priceNet.currency;
					}

					return (sizeData.quantity || 0) * (sizeData.priceNet.amount || 0);
				}
			});

			orderTotal += sizesAmount.reduce((prev, next) => prev + next);
		}
	});

	return {
		amount: orderTotal,
		amountFormat: formatCurrency(orderTotal, orderCurrency),
	};
};

export const orderQuantityByProducts = (orderProducts?: OrderProduct[]) => {
	let productSizesQuantity = 0;

	if (!orderProducts) {
		return 0;
	}

	_.forEach(orderProducts, (orderProduct) => {
		if (orderProduct.sizes) {
			let sizesQuantity = _.map(
				orderProduct.sizes,
				(sizeData) => sizeData.quantity || 0,
			);

			productSizesQuantity += sizesQuantity.reduce((prev, next) => prev + next);
		}
	});

	return productSizesQuantity;
};

export const orderTotalBySizes = (
	productSizes?: ProductSize[],
): ISizesTotal => {
	let productSizesTotal = 0;
	let orderCurrency: string | undefined;

	if (!productSizes || productSizes.length === 0) {
		return {
			amount: productSizesTotal,
			amountFormat: formatCurrency(productSizesTotal, orderCurrency),
		};
	}

	let sizesAmount = _.map(productSizes, (sizeData) => {
		if (!sizeData.priceNet || sizeData.priceNet.amount === -1) {
			return 0;
		} else {
			if (!orderCurrency) {
				orderCurrency = sizeData.priceNet.currency;
			}

			return (sizeData.quantity || 0) * (sizeData.priceNet.amount || 0);
		}
	});

	productSizesTotal = sizesAmount.reduce((prev, next) => prev + next);

	return {
		amount: productSizesTotal,
		amountFormat: formatCurrency(productSizesTotal, orderCurrency),
	};
};

export const orderLineAmountByProductSize = (
	productSize?: IProductSizeAggregate,
): ISizesTotal => {
	let productSizeTotal = 0;
	let orderCurrency: string | undefined;

	if (!productSize) {
		return {
			amount: productSizeTotal,
			amountFormat: formatCurrency(productSizeTotal, orderCurrency),
		};
	}

	if (!productSize.priceNet || productSize.priceNet.amount === -1) {
		productSizeTotal = 0;
	} else {
		if (!orderCurrency) {
			orderCurrency = productSize.priceNet.currency;
		}

		productSizeTotal =
			(productSize.quantity || 0) * (productSize.priceNet.amount || 0);
	}

	return {
		amount: productSizeTotal,
		amountFormat: formatCurrency(productSizeTotal, orderCurrency),
	};
};

export const orderQuantityBySizes = (productSizes?: ProductSize[]) => {
	let productSizesQuantity = 0;

	if (!productSizes) {
		return 0;
	}

	let sizesQuantity = _.map(productSizes, (sizeData) => sizeData.quantity || 0);

	productSizesQuantity = sizesQuantity.reduce((prev, next) => prev + next);

	return productSizesQuantity;
};

export const manipulateSizeQuantity = (
	productSize: ProductSize,
	payload: OrderProduct,
	toManipulate: boolean,
) => {
	let payloadSizeMatch =
		payload.sizes && payload.sizes.find((entry) => entry.id === productSize.id);
	console.log(payloadSizeMatch);
	return payloadSizeMatch && toManipulate
		? (productSize.quantity || 0) + (payloadSizeMatch.quantity || 0)
		: productSize.quantity || 0;
};

export const productEqualityComparer = (
	value: OrderProduct,
	comparer: OrderProduct,
) =>
	value.id === comparer.id &&
	value.rowId !== comparer.rowId &&
	value.facility?.id === comparer.facility?.id &&
	formatStandardDate(value.deliveryDate) ===
		formatStandardDate(comparer.deliveryDate);

export const generateProgressiveValue = (orderProducts?: OrderProduct[]) => {
	if (!orderProducts) {
		return 100;
	}

	let progressiveMax = _.maxBy(orderProducts, (entry) => entry.rowId);

	return (progressiveMax?.rowId || 0) + 100;
};
